/**
 * Compras — Cotação por item (D2 + Fase 2, 15/09/2026). Tela operacional do comprador.
 *
 * Lista de cotações → por item: uma LINHA por fornecedor (sugerido pela família, ou
 * acrescentado à mão) com o que voltou do WhatsApp — preço unitário e % de desconto —
 * e o que o servidor calcula: total, total com desconto, margem. A tela ordena por
 * PREÇO ou por MARGEM, marca as duas pontas (mais barato · melhor margem) e DIZ quando
 * não são o mesmo fornecedor. O sistema NUNCA escolhe: a pessoa escolhe, e fica
 * registrado quem e quando. Cálculo vem do backend (`getCotacaoFull`). CENTAVOS.
 *
 * Fase 2: a lista vem do LEVANTAMENTO (código + vendido); estoque, marketplace e varejo
 * PULAM a cotação ("Onde comprou"); o custo FECHADO e o RECEBIDO ficam os dois, com a
 * diferença; ganho de negociação e economia % nunca são digitados.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Trash2 } from 'lucide-react';

import {
  Alert,
  Badge,
  BuscaCombobox,
  Button,
  Card,
  EmptyState,
  ErroCampo,
  HelpTip,
  Input,
  Label,
  LinhaRecolhivel,
  PageHeader,
  Select,
  SelectOption,
  Switch,
  ThOrdenavel,
  useOrdenacao,
  ordenarLista,
  useToast,
  useConfirmDialog,
} from '@/components/ui';
import { CollapsibleSection } from './CollapsibleSection';
import { BotaoDesfazerAceitacao, BotaoManter } from './DivergenciaAceita';
import { formatCurrency, parseNumeroBr, reaisStringToCentavos } from '@/lib/format';
import { dataHoraBR } from '@/lib/datas-brasilia';
import { errMsg } from '@/lib/api-error';
import { ApiError } from '@/lib/api';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import {
  useTiposItem,
  useFornecedores,
  useFornecedoresPorTipo,
  useEmpresasCompras,
  useFormasPagamento,
  empresaLabel,
  useCotacoes,
  useCotacaoFull,
  useDeleteCotacao,
  useCotacaoActions,
  ONDE_COMPROU_LABEL,
  type BlocoComputed,
  type CotacaoItemFull,
  type FornecedorParente,
  type OndeComprou,
  type UpdateCotacaoItemInput,
  type RealizadoCategoria,
} from '@/lib/compras-store';
import { fraseDosParentes, nomeComLugar } from './nome-do-fornecedor';
import { useSolicitacaoItensParaCotacao } from '@/lib/solicitacoes-compra-store';

const CATEGORIA_REALIZADO_LABEL: Record<RealizadoCategoria, string> = {
  material: 'Material',
  mao_obra_terceira: 'Mão de obra (terceiro)',
  mao_obra_propria: 'Mão de obra (própria)',
  logistica: 'Logística',
  outros: 'Outros',
};

/**
 * Fase 2 (decisão do Matheus, 15/09/2026): a linha do fornecedor carrega a RAZÃO SOCIAL
 * (duas empresas usam a marca "INNOVATIE" — só ela distingue) e os PARENTES (mesma raiz de
 * CNPJ: matriz e filial). Sem CNPJ válido, `parentes` vem vazio e a tela não mostra selo —
 * não diz "sem parentes", porque não sabe.
 */
type FornecedorLite = { id: string; nome: string; razaoSocial?: string | null; parentes?: FornecedorParente[]; lugar?: string | null };


function brl(c: number): string {
  return formatCurrency(c / 100);
}
function pct(v: number): string {
  return `${v.toFixed(1).replace('.', ',')}%`;
}
/** +R$ 10,00 / −R$ 10,00 — a diferença com o sinal na frente. */
function brlComSinal(c: number): string {
  return `${c < 0 ? '−' : '+'}${brl(Math.abs(c))}`;
}
// CO-4: `toCent` foi extraído pra `lib/format` (reaisStringToCentavos) — fonte
// única. Alias local mantém os call sites curtos.
const toCent = reaisStringToCentavos;

/** O campo que o servidor apontou num 400 (`{ error, campo, message }`). */
function campoDoErro(e: unknown): { campo: string; mensagem: string } | null {
  if (!(e instanceof ApiError)) return null;
  const body = e.response as { campo?: string; message?: string } | undefined;
  return body?.campo ? { campo: body.campo, mensagem: body.message ?? 'Confira este campo.' } : null;
}

export function ComprasCotacoesPage() {
  const comprasOn = useMyFeatureFlag('COMPRAS_ENABLED');
  // FX-6: deep-link `?cotacao=<id>` abre a cotação direto (vindo do Quadro:
  // "Cotar"/"Ver cotação"). Some da URL ao voltar pra lista.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('cotacao'));

  useEffect(() => {
    const fromUrl = searchParams.get('cotacao');
    if (fromUrl && fromUrl !== selectedId) setSelectedId(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const back = () => {
    setSelectedId(null);
    if (searchParams.has('cotacao')) {
      searchParams.delete('cotacao');
      setSearchParams(searchParams, { replace: true });
    }
  };

  if (!comprasOn) {
    return (
      <section>
        <EmptyState title="Compras indisponível" description="O módulo de Compras ainda não está ativado para a sua conta." />
      </section>
    );
  }
  return (
    /* 🔴 #340 (decisão do Matheus, 17/09): "alvo de toque: 44 px na tela TODA de Compras — é a régua que ele mesmo criou", e "metade
       a 44 e metade a 36 fica pior que tudo a 36". No celular, todo campo, seletor e botão desta tela sobe para 44 px, de uma vez,
       pela raiz — e não campo a campo. Em tela larga nada muda (lá é mouse). */
    <section className="max-sm:[&_input:not([type=checkbox]):not([type=radio])]:min-h-[44px] max-sm:[&_select]:min-h-[44px] max-sm:[&_button]:min-h-[44px]">
      {selectedId ? (
        <CotacaoBuilder id={selectedId} onBack={back} />
      ) : (
        <CotacoesList onOpen={setSelectedId} />
      )}
    </section>
  );
}

// ─── Lista ──────────────────────────────────────────────────────────────
function CotacoesList({ onOpen }: { onOpen: (id: string) => void }) {
  const { data, isLoading, isError, refetch } = useCotacoes();
  const del = useDeleteCotacao();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const items = data?.items ?? [];

  async function excluir(id: string, t: string) {
    const ok = await confirm({ title: 'Excluir cotação?', message: `Excluir "${t}" e seus itens?`, confirmLabel: 'Excluir', variant: 'destructive' });
    if (!ok) return;
    del.mutate(id, { onError: (e) => toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', { variant: 'error' }) });
  }

  return (
    <div>
      {/* FASE-C: PageHeader canônico (crumb + h1 + subtítulo + ações). */}
      {/* Fase 2: a cotação NASCE do Quadro (Cotar no card da obra) — nunca tela vazia. "Nova cotação" por título morreu. */}
      <PageHeader
        crumb="Compras"
        title="Cotações"
        subtitle="Compare preço e margem por item, fornecedor a fornecedor, e escolha caso a caso. A cotação nasce no Quadro de Compras, no card da obra."
      />

      {isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : isError ? (
        <Card variant="flat">
          <EmptyState
            title="Não foi possível carregar as cotações"
            description="Tente de novo em instantes."
            action={<Button size="sm" variant="secondary" onClick={() => refetch()}>Tentar de novo</Button>}
          />
        </Card>
      ) : items.length === 0 ? (
        <Card variant="flat"><EmptyState title="Nenhuma cotação ainda" description="A cotação nasce no Quadro de Compras: abra o card da obra e clique em Cotar." /></Card>
      ) : (
        <Card variant="flat" className="overflow-hidden">
          <ul>
            {items.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 border-b border-line dark:border-line-dark last:border-b-0 px-3 py-2.5">
                <button type="button" onClick={() => onOpen(c.id)} className="flex-1 text-left flex items-center gap-2 min-w-0 min-h-[44px]" data-testid={`cotacao-row-${c.id}`}>
                  {c.numeroHumano != null && (
                    <span className="text-xs text-ink-3 dark:text-ink-dark-3 tabular-nums shrink-0">#{c.numeroHumano}</span>
                  )}
                  <span className="text-sm text-ink-1 dark:text-ink-dark-1 truncate">{c.titulo}</span>
                  <Badge variant={c.status === 'fechada' ? 'neutral' : 'success'} size="sm">{c.status === 'fechada' ? 'Fechada' : 'Aberta'}</Badge>
                </button>
                <Button size="sm" variant="ghost" className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark" onClick={() => excluir(c.id, c.titulo)}>Excluir</Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ─── Builder ────────────────────────────────────────────────────────────
function CotacaoBuilder({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, isLoading, isError, refetch } = useCotacaoFull(id);
  const actions = useCotacaoActions(id);
  const tiposQuery = useTiposItem();
  const fornecedoresQuery = useFornecedores();
  const toast = useToast();

  const fornecedorNome = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of fornecedoresQuery.data?.items ?? []) m.set(f.id, f.nome);
    return m;
  }, [fornecedoresQuery.data]);
  const todosFornecedores: FornecedorLite[] = useMemo(
    () => (fornecedoresQuery.data?.items ?? []).map((f) => ({ id: f.id, nome: f.nome, razaoSocial: f.razaoSocial, parentes: f.parentes, lugar: f.lugar })),
    [fornecedoresQuery.data],
  );
  /** Fase 2: o cadastro inteiro por id — para a linha de um fornecedor que só tem bloco (fora da sugestão). */
  const fornecedorPorId = useMemo(
    () => new Map(todosFornecedores.map((f) => [f.id, f])),
    [todosFornecedores],
  );
  const tipos = useMemo(() => (tiposQuery.data?.items ?? []).filter((t) => t.ativo), [tiposQuery.data]);

  if (isLoading) return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando cotação…</p>;
  // CO-3: trata erro de carregamento (antes ficava em "Carregando…" pra sempre).
  if (isError || !data) {
    return (
      <Card variant="flat">
        <EmptyState
          title="Não foi possível carregar a cotação"
          description="Tente de novo ou volte para a lista."
          action={
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => refetch()}>Tentar de novo</Button>
              <Button size="sm" variant="ghost" onClick={onBack}>Voltar</Button>
            </div>
          }
        />
      </Card>
    );
  }
  const { cotacao, itens } = data;

  return (
    <div className="flex flex-col gap-3">
      <Card variant="flat" className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="Voltar">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="text-xl font-display text-ink-1 dark:text-ink-dark-1 flex-1 min-w-[160px] truncate">
            {cotacao.numeroHumano != null && (
              <span className="text-ink-3 dark:text-ink-dark-3 tabular-nums mr-1.5">#{cotacao.numeroHumano}</span>
            )}
            {cotacao.titulo}
          </span>
          <Select
            size="sm"
            aria-label="Situação da cotação"
            value={cotacao.status}
            onChange={(e) =>
              actions.update.mutate(
                { status: e.target.value as 'aberta' | 'fechada' },
                { onError: (err) => toast.show(errMsg(err) ?? 'Não deu certo. Tente de novo.', { variant: 'error' }) },
              )
            }
            className="w-32"
          >
            <SelectOption value="aberta">Aberta</SelectOption>
            <SelectOption value="fechada">Fechada</SelectOption>
          </Select>
          <HelpTip texto="Aberta: a cotação aceita preço, fornecedor, recebimento e efetivação. Fechada: nada se grava nela, para ninguém mudar uma cotação encerrada por engano. Para mudar alguma coisa, volte para Aberta." />
        </div>
        {/* 🔴 #377: fechada TRAVA (antes só mudava o selo). A tela diz, antes de a pessoa tentar. */}
        {cotacao.status === 'fechada' && (
          <p
            role="status"
            className="mt-2 rounded-lg border border-line bg-paper-warm p-2 text-sm text-ink-1 dark:border-line-dark dark:bg-paper-dark-warm dark:text-ink-dark-1"
            data-testid="cotacao-fechada-aviso"
          >
            Esta cotação está fechada: nada se grava nela. Para mudar alguma coisa, volte a situação para Aberta.
          </p>
        )}
        {itens.length > 0 && (
          <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
            Cotação parcial é normal: cada item fecha com quem cotou aquele item.{' '}
            Fechados: <span className="tabular-nums text-ink-1 dark:text-ink-dark-1">{itens.filter((i) => i.valorCompradoEfetivoCentavos != null).length}/{itens.length}</span> · entregues:{' '}
            <span className="tabular-nums text-ink-1 dark:text-ink-dark-1">{itens.filter((i) => i.entregue).length}/{itens.length}</span>
          </p>
        )}
      </Card>

      {itens.length === 0 ? (
        // Fase 2: "Adicionar item" em texto livre morreu — a lista vem do levantamento (só item com código).
        <Card variant="flat">
          <EmptyState title="Esta cotação está sem itens" description="Os itens chegam pelo Quadro de Compras: no card da obra, use Cotar ou Juntar a esta cotação." />
        </Card>
      ) : (
        itens.map((item) => (
          <CotacaoItemCard key={item.id} cotacaoId={id} item={item} tipos={tipos} fornecedorNome={fornecedorNome} fornecedorPorId={fornecedorPorId} todosFornecedores={todosFornecedores} />
        ))
      )}
    </div>
  );
}

// ─── Item ───────────────────────────────────────────────────────────────
type ChaveOrdem = 'fornecedor' | 'preco' | 'margem';

function CotacaoItemCard({
  cotacaoId,
  item,
  tipos,
  fornecedorNome,
  fornecedorPorId,
  todosFornecedores,
}: {
  cotacaoId: string;
  item: CotacaoItemFull;
  tipos: Array<{ id: string; nome: string }>;
  fornecedorNome: Map<string, string>;
  fornecedorPorId: Map<string, FornecedorLite>;
  todosFornecedores: FornecedorLite[];
}) {
  const actions = useCotacaoActions(cotacaoId);
  const toast = useToast();
  const confirm = useConfirmDialog();
  const sugeridos = useFornecedoresPorTipo(item.tipoItemId);
  const empresas = useEmpresasCompras();
  const formasPagamento = useFormasPagamento();
  const solItens = useSolicitacaoItensParaCotacao();
  // Fase 2 (decisão do Matheus, 15/09/2026): a alçada de aprovação do Financeiro MORREU —
  // "Liberar compra" libera; a efetivação grava. Sem política, sem fila, sem "pendente/recusado".

  const [qtd, setQtd] = useState(String(item.quantidade));
  const [venda, setVenda] = useState(String(item.vendaTotalCentavos / 100));
  // 17/09 — a DIVISÃO: quantas saem do estoque e quanto custa cada uma (digitado; nada do estoque do Omie).
  const [qtdEstoque, setQtdEstoque] = useState(String(item.qtdEstoque ?? 0));
  const [custoEstoque, setCustoEstoque] = useState(item.custoEstoqueUnitCentavos != null ? String(item.custoEstoqueUnitCentavos / 100) : '');
  useEffect(() => { setQtdEstoque(String(item.qtdEstoque ?? 0)); }, [item.qtdEstoque]);
  useEffect(() => { setCustoEstoque(item.custoEstoqueUnitCentavos != null ? String(item.custoEstoqueUnitCentavos / 100) : ''); }, [item.custoEstoqueUnitCentavos]);
  // CO-4: re-sincroniza os inputs controlados quando o item muda por fora
  // (ex.: outro save recalcula o full, ou auto-sync cross-tab) — antes ficavam
  // com o valor antigo digitado.
  useEffect(() => { setQtd(String(item.quantidade)); }, [item.quantidade]);
  useEffect(() => { setVenda(String(item.vendaTotalCentavos / 100)); }, [item.vendaTotalCentavos]);
  // FX-4: faturamento direto exige fornecedor (já gateia a seção) + empresa.
  // Ligar o toggle só REVELA o select de empresa (estado local); só faz o PATCH
  // (faturamentoDireto:true + empresa) quando a empresa é escolhida — sem erro
  // pós-clique. `fatDiretoAberto` reflete o estado salvo OU o "abrindo".
  const [fatPendente, setFatPendente] = useState(false);
  const fatDiretoAberto = item.faturamentoDireto || fatPendente;
  useEffect(() => { if (item.faturamentoDireto) setFatPendente(false); }, [item.faturamentoDireto]);

  // Fase 2 — "Onde comprou": o nome do marketplace/loja e o erro NO CAMPO (o servidor aponta).
  const [ondeNome, setOndeNome] = useState(item.ondeComprouNome ?? '');
  useEffect(() => { setOndeNome(item.ondeComprouNome ?? ''); }, [item.ondeComprouNome]);
  const [erroCampo, setErroCampo] = useState<{ campo: string; mensagem: string } | null>(null);
  // "Onde comprou" escolhido mas ainda sem o nome — o select mostra o novo enquanto o nome não vem.
  const [ondePendente, setOndePendente] = useState<OndeComprou | null>(null);
  const ondeMostrado = ondePendente ?? item.ondeComprou;
  // Fase 2 — o recebido (o que veio na nota).
  const [recebido, setRecebido] = useState(item.custoRecebidoCentavos != null ? String(item.custoRecebidoCentavos / 100) : '');
  useEffect(() => { setRecebido(item.custoRecebidoCentavos != null ? String(item.custoRecebidoCentavos / 100) : ''); }, [item.custoRecebidoCentavos]);
  // Fase 2 — fornecedores acrescentados à mão (fora da sugestão), só até ganharem preço.
  const [extras, setExtras] = useState<FornecedorLite[]>([]);
  const [acrescentarId, setAcrescentarId] = useState('');

  // Linhas = fornecedores sugeridos (pela família) ∪ os que já têm bloco ∪ os acrescentados.
  const blocoByForn = useMemo(() => new Map(item.blocos.map((b) => [b.fornecedorId, b])), [item.blocos]);
  const melhorFornId = item.melhorMargemIdx >= 0 ? item.blocos[item.melhorMargemIdx]?.fornecedorId ?? null : null;
  const baratoFornId = item.maisBaratoIdx >= 0 ? item.blocos[item.maisBaratoIdx]?.fornecedorId ?? null : null;
  // FX-36: Nº Pedido/NF do fornecedor escolhido (obrigatório na efetivação).
  const escolhidoBloco = item.fornecedorEscolhidoId ? blocoByForn.get(item.fornecedorEscolhidoId) : undefined;
  const [pedidoNfInput, setPedidoNfInput] = useState(escolhidoBloco?.numeroPedidoNf ?? '');
  useEffect(() => { setPedidoNfInput(escolhidoBloco?.numeroPedidoNf ?? ''); }, [escolhidoBloco?.numeroPedidoNf]);
  const fornecedores: FornecedorLite[] = useMemo(() => {
    const map = new Map<string, FornecedorLite>();
    for (const f of sugeridos.data?.items ?? []) map.set(f.id, { id: f.id, nome: f.nome, razaoSocial: f.razaoSocial, parentes: f.parentes, lugar: f.lugar });
    // garante linha para blocos de fornecedores que não estão na sugestão (ex.: tipo trocado)
    for (const b of item.blocos) if (!map.has(b.fornecedorId)) {
      const cadastro = fornecedorPorId.get(b.fornecedorId);
      map.set(b.fornecedorId, cadastro ?? { id: b.fornecedorId, nome: fornecedorNome.get(b.fornecedorId) ?? '—' });
    }
    for (const f of extras) if (!map.has(f.id)) map.set(f.id, f);
    return [...map.values()];
  }, [sugeridos.data, item.blocos, fornecedorNome, fornecedorPorId, extras]);

  // Fase 2 — ordenar por PREÇO ou por MARGEM (o trio canônico; linha sem preço vai ao fim).
  const ordem = useOrdenacao<ChaveOrdem>('margem', 'desc');
  const VALOR_POR_CHAVE: Record<ChaveOrdem, (f: FornecedorLite) => string | number | null> = {
    fornecedor: (f) => f.nome,
    preco: (f) => blocoByForn.get(f.id)?.cotacaoUnitCentavos || null,
    margem: (f) => (blocoByForn.get(f.id)?.cotacaoUnitCentavos ? blocoByForn.get(f.id)!.margemComDescontoCentavos : null),
  };
  const linhas = ordenarLista(fornecedores, VALOR_POR_CHAVE[ordem.chave], ordem.sentido);
  const paraAcrescentar = useMemo(
    () => todosFornecedores.filter((f) => !fornecedores.some((l) => l.id === f.id)),
    [todosFornecedores, fornecedores],
  );

  function patchItem(patch: UpdateCotacaoItemInput, ok?: () => void) {
    // CO-4: confirmação dos saves silenciosos (blur/toggle/select).
    actions.updateItem.mutate(
      { itemId: item.id, patch },
      {
        onSuccess: () => { setErroCampo(null); toast.show('Salvo.', { variant: 'success' }); ok?.(); },
        onError: (e) => {
          const c = campoDoErro(e);
          if (c) {
            // O servidor apontou o campo: o erro vai NO CAMPO, com foco — e SÓ lá. Achado na auditoria no celular
            // (16/09): em 375 px o toast com a mesma frase cobria a mensagem do campo e a barra inferior, e as duas
            // ficavam ilegíveis ("nada cobre nada"). Toast fica para o erro que não tem campo.
            setErroCampo(c);
            if (c.campo === 'faturamentoDireto') {
              setFatPendente(false); // #326: o seletor da empresa não fica aberto à toa
              document.getElementById(`fat-${item.id}`)?.focus();
            }
            return;
          }
          toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', { variant: 'error' });
        },
      },
    );
  }
  async function mudarOndeComprou(novo: OndeComprou) {
    if (novo === item.ondeComprou) return;
    const precisaNome = novo === 'marketplace' || novo === 'varejo';
    if (precisaNome && !ondeNome.trim()) {
      // A tela diz ANTES: sem o nome o servidor recusa — o campo abre e pede.
      setErroCampo({ campo: 'ondeComprouNome', mensagem: novo === 'marketplace' ? 'Diga qual marketplace (ex.: Mercado Livre).' : 'Diga qual loja.' });
      setOndePendente(novo);
      return;
    }
    if (novo !== 'cotacao' && item.fornecedorEscolhidoId) {
      const ok = await confirm({
        title: 'Desfazer a escolha do fornecedor?',
        message: `Este item passa a "${ONDE_COMPROU_LABEL[novo]}" e deixa de ter fornecedor da cotação: a escolha de ${fornecedorNome.get(item.fornecedorEscolhidoId) ?? 'fornecedor'} é desfeita. As linhas de preço ficam guardadas.`,
        confirmLabel: 'Trocar',
      });
      if (!ok) return;
      patchItem({ ondeComprou: novo, ondeComprouNome: precisaNome ? ondeNome.trim() : null, fornecedorEscolhidoId: null }, () => setOndePendente(null));
      return;
    }
    patchItem({ ondeComprou: novo, ondeComprouNome: precisaNome ? ondeNome.trim() : null }, () => setOndePendente(null));
  }
  function salvarOndeNome() {
    const nome = ondeNome.trim();
    const alvo = ondePendente ?? item.ondeComprou;
    if (alvo !== 'marketplace' && alvo !== 'varejo') return;
    if (!nome) {
      setErroCampo({ campo: 'ondeComprouNome', mensagem: alvo === 'marketplace' ? 'Diga qual marketplace (ex.: Mercado Livre).' : 'Diga qual loja.' });
      return;
    }
    if (nome === item.ondeComprouNome && alvo === item.ondeComprou) return;
    // marketplace/varejo nunca têm fornecedor da cotação — desfaz junto (o servidor recusa separado).
    const patch: UpdateCotacaoItemInput = { ondeComprou: alvo, ondeComprouNome: nome };
    if (item.fornecedorEscolhidoId) patch.fornecedorEscolhidoId = null;
    patchItem(patch, () => setOndePendente(null));
  }
  function lancarRealizado() {
    actions.lancarRealizado.mutate(item.id, {
      // FX-35: liberar NÃO grava o realizado — a efetivação grava.
      onSuccess: () => toast.show('Compra liberada. Marque "compra efetivada" ao fechar o pedido.', { variant: 'success' }),
      onError: (e) => toast.show(errMsg(e) ?? 'Não deu pra lançar.', { variant: 'error' }),
    });
  }
  // 🔴 18/09 (decisão 1 do buraco): um clique aplica a quantidade do levantamento. O número vem do servidor na hora do clique.
  function aplicarLevantamento() {
    actions.aplicarLevantamento.mutate(item.id, {
      onSuccess: ({ aplicado }) =>
        toast.show(`Quantidade trocada de ${aplicado.de} para ${aplicado.para}, como no levantamento. Confira o preço com o fornecedor.`, { variant: 'success' }),
      onError: (e) => toast.show(errMsg(e) ?? 'Não deu para aplicar. Tente de novo.', { variant: 'error' }),
    });
  }
  function efetivarCompra() {
    actions.efetivar.mutate(item.id, {
      onSuccess: () => toast.show('Compra efetivada — custo lançado no realizado.', { variant: 'success' }),
      onError: (e) => toast.show(errMsg(e) ?? 'Não deu pra efetivar.', { variant: 'error' }),
    });
  }
  async function remover() {
    const ok = await confirm({ title: 'Remover item?', message: `Remover "${item.descricao}" da cotação?`, confirmLabel: 'Remover', variant: 'destructive' });
    if (!ok) return;
    actions.deleteItem.mutate(item.id, { onError: (e) => toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', { variant: 'error' }) });
  }

  // FX-27: resumo unificado do bloco "Pagamento" — forma de pagamento + fat. direto.
  const formaPagamentoNome =
    formasPagamento.data?.items.find((f) => f.id === item.formaPagamentoId)?.nome ?? null;
  const pagamentoResumo = (() => {
    if (!item.fornecedorEscolhidoId) return null;
    const parts: string[] = [];
    if (formaPagamentoNome) parts.push(formaPagamentoNome);
    if (item.faturamentoDireto) {
      parts.push('fat. direto');
      if (item.faturamento) {
        parts.push(
          item.faturamento.margemComCreditoRetidoCentavos === null
            ? 'imposto do distribuidor não preenchido'
            : `retido ${brl(item.faturamento.margemComCreditoRetidoCentavos)}`,
        );
      }
    }
    return parts.length ? parts.join(' · ') : 'Definir forma de pagamento';
  })();
  // FX-35: aprovado = liberado (a efetivar); efetivado (realizadoCustoId) = comprado.
  const realizadoResumo = item.compraEfetivada
    ? 'compra efetivada'
    : item.realizadoAprovacaoStatus === 'aprovado'
      ? 'liberada — a efetivar'
      : 'não liberada';
  // 17/09: o estado da entrega mora AQUI, num lugar só (a etiqueta "A caminho" saiu da faixa de fechamento). E "a caminho"
  // só depois de a compra ser efetivada: item que ninguém comprou não está a caminho de lugar nenhum.
  const logisticaResumo =
    [
      item.entregue ? 'entregue' : item.recebido ? 'recebido' : item.compraEfetivada ? 'a caminho' : item.qtdComprar > 0 ? 'nada comprado ainda' : 'sai do estoque',
      !item.entregue && item.previsaoChegada ? `previsão ${item.previsaoChegada}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  const codigoDoItem = item.codigo ?? item.codigoProduto;
  // 17/09: "na cotação" = a parte COMPRADA vem de fornecedor cotado. Tudo do estoque → não se cota.
  const temCompra = item.qtdComprar > 0;
  const naCotacao = item.ondeComprou === 'cotacao' && temCompra;
  const parteDoEstoque = (item.qtdEstoque ?? 0) > 0;
  const fraseDaDivisao = parteDoEstoque
    ? `${item.quantidade} ${item.quantidade === 1 ? 'unidade' : 'unidades'}: ${item.qtdEstoque} do estoque, ${item.qtdComprar} ${item.qtdComprar === 1 ? 'comprada' : 'compradas'}`
    : null;
  const nomeBarato = baratoFornId ? fornecedorNome.get(baratoFornId) ?? fornecedores.find((f) => f.id === baratoFornId)?.nome ?? '—' : null;
  const nomeMelhor = melhorFornId ? fornecedorNome.get(melhorFornId) ?? fornecedores.find((f) => f.id === melhorFornId)?.nome ?? '—' : null;

  // 🔴 #339 (20/09): o estado da linha recolhida — o que a pessoa precisa ver ANTES de abrir o item.
  // 🔴 #377: a linha de uma solicitação CANCELADA diz isso antes de tudo — e nada mais se grava nela (o servidor recusa).
  const estadoDaLinha = item.solicitacaoCancelada
    ? 'solicitação cancelada'
    : item.compraEfetivada
    ? 'comprado'
    : item.realizadoAprovacaoStatus === 'aprovado'
      ? 'liberado'
      : item.fornecedorEscolhidoId
        ? 'escolhido'
        : item.blocos.length > 0
          ? 'em cotação'
          : 'a cotar';

  return (
    /* 🔴 #339/#340: a MESMA linha recolhida do Levantamento. A decisão do Matheus para a cotação (17/09, BUGS #339): *"o ITEM
       NASCE RECOLHIDO"*, em qualquer largura, com a linha **descrição · qtd · fechado · margem · estado**; a divisão aparece SÓ nos
       itens divididos ("2 do estoque, 3 compradas"). Abrir é um toque, e o item aberto FICA aberto. Cabeçalho de 44 px (#340). */
    <LinhaRecolhivel
      recolhe="sempre"
      className="bg-paper-surface dark:bg-paper-dark-surface"
      data-testid={`cotacao-item-${item.id}`}
      titulo={item.descricao}
      quantidade={fraseDaDivisao ?? `${item.quantidade} un`}
      estado={
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          {/* sem dado, a linha DIZ o que falta (régua do #311) — na forma CURTA: a frase inteira ("falta o preço da compra
              (escolha um fornecedor…)") custava 188 px de cabeçalho em 375 px; ela continua a um toque, dentro do item. */}
          <span data-testid={`resumo-fechado-${item.id}`}>
            fechado {item.custoTotalCentavos != null ? brl(item.custoTotalCentavos) : 'sem preço'}
          </span>
          <span
            className={item.margemFinalCentavos != null && item.margemFinalCentavos < 0 ? 'text-red' : undefined}
            data-testid={`resumo-margem-${item.id}`}
          >
            margem{' '}
            {item.margemFinalCentavos != null ? `${brl(item.margemFinalCentavos)} (${pct(item.margemFinalPct!)})` : 'a calcular'}
          </span>
          <Badge variant="neutral" size="sm">{estadoDaLinha}</Badge>
        </span>
      }
    >
      {/* 🔴 #377: a solicitação desta linha foi cancelada. O que já foi cotado fica como registro; nada mais se grava. */}
      {item.solicitacaoCancelada && (
        <div
          role="status"
          className="mb-3 rounded-lg border border-line bg-paper-warm p-2 text-sm text-ink-1 dark:border-line-dark dark:bg-paper-dark-warm dark:text-ink-dark-1"
          data-testid={`cancelada-na-cotacao-${item.id}`}
        >
          <p className="break-words">{item.solicitacaoCancelada.frase}</p>
          <p className="mt-1 break-words text-ink-2 dark:text-ink-dark-2">
            {item.solicitacaoCancelada.motivo ? `Motivo: ${item.solicitacaoCancelada.motivo}` : 'Sem motivo registrado.'}
          </p>
        </div>
      )}
      {/* 🔴 18/09 (o buraco dos itens já enviados): o levantamento mudou depois da cotação, o item saiu, ou comprou-se a mais.
          ❌ Nada muda sozinho: o fornecedor cotou preço unitário e pode ter dado desconto por volume. Quem decide é o Compras. */}
      {item.avisoLevantamento && item.avisoLevantamento.tipo === 'aceita' && (
        // 🔴 #367: a divergência ACEITA — fato, sem alarme. A diferença continua escrita; o Compras pode desfazer.
        <div
          className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-paper-subtle p-2 text-sm text-ink-2 dark:border-line-dark dark:bg-paper-dark-subtle dark:text-ink-dark-2"
          data-testid={`aceita-levantamento-${item.id}`}
        >
          <span className="min-w-0 flex-1 break-words">
            <strong className="font-medium">Levantamento: </strong>
            {item.avisoLevantamento.frase}.
          </span>
          <BotaoDesfazerAceitacao itemId={item.id} desfazer={actions.desfazerAceitacao} />
        </div>
      )}
      {item.avisoLevantamento && item.avisoLevantamento.tipo !== 'aceita' && (
        <div
          role="status"
          className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-warning/40 bg-warning-tint p-2 text-sm text-ink-1 dark:text-ink-dark-1"
          data-testid={`aviso-levantamento-${item.id}`}
        >
          <span className="min-w-0 flex-1 break-words">
            <strong className="font-medium">Levantamento: </strong>
            {item.avisoLevantamento.frase}.
          </span>
          {item.avisoLevantamento.tipo === 'ajustar' && item.avisoLevantamento.para != null && !item.compraEfetivada && (
            <span className="inline-flex items-center gap-1">
              <Button
                size="sm"
                className="min-h-[44px] sm:min-h-0"
                loading={actions.aplicarLevantamento.isPending}
                onClick={aplicarLevantamento}
                data-testid={`aplicar-levantamento-${item.id}`}
              >
                Aplicar: {item.avisoLevantamento.para}
              </Button>
              <HelpTip texto="O levantamento mudou depois que este item foi cotado. Nada muda sozinho: o fornecedor pode ter dado preço por volume. Aplicar troca a quantidade daqui (e a da solicitação) pela que o levantamento pede; o preço unitário fica como está, e você confere com o fornecedor." />
            </span>
          )}
          {/* 🔴 #367: a diferença é de propósito? O Compras mantém, com motivo. */}
          {item.avisoLevantamento.manter != null && (
            <BotaoManter itemId={item.id} manter={item.avisoLevantamento.manter} aceitar={actions.aceitarDivergencia} />
          )}
        </div>
      )}
      {/* Camada 1 — Topo: o item (com o código e o que foi vendido) + venda total em destaque. */}
      {/* ⚠️ 17/09 (auditoria no celular): sem `flex-wrap`, o bloco do Vendido (203 px, `shrink-0`) COBRIA o campo do item em 375 px —
          medido com getBoundingClientRect: input x 29–179, vendido x 87–290, na mesma faixa de altura. Agora ele desce para a linha de baixo. */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="w-full sm:w-auto sm:flex-1 min-w-0 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[150px]">
            <Label>Item</Label>
            <Input
              size="sm"
              aria-label="Descrição do item"
              defaultValue={item.descricao}
              onBlur={(e) => { const d = e.target.value.trim(); if (d && d !== item.descricao) patchItem({ descricao: d }); }}
            />
            <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {codigoDoItem ? (
                <span className="tabular-nums" data-testid={`codigo-${item.id}`}>código {codigoDoItem}</span>
              ) : item.projetoItemId ? (
                <span className="text-warning dark:text-[#D9AE6B]">sem código no levantamento</span>
              ) : null}
              {item.quantidadeVendida != null && item.vendaUnitCentavos != null ? null : item.projetoItemId ? (
                <span className="text-warning dark:text-[#D9AE6B]" data-testid={`sem-venda-${item.id}`}>
                  item novo no levantamento — sem valor vendido
                  <HelpTip texto="Este item não estava na proposta vendida: entrou no levantamento depois. Não há preço de venda para comparar; a margem dele sai zerada até alguém informar a venda total." className="ml-1" />
                </span>
              ) : null}
            </p>
          </div>
          <div className="w-full sm:w-44">
            <Label>Família <HelpTip texto="A família do produto (cabo, câmera, infra…). É por ela que o sistema sugere quais fornecedores chamar — os que estão cadastrados como fornecedores dessa família na ficha do cliente, aba Fornecedor." /></Label>
            <Select size="sm" aria-label="Família do item" value={item.tipoItemId ?? ''} onChange={(e) => patchItem({ tipoItemId: e.target.value || null })}>
              <SelectOption value="">— sem família —</SelectOption>
              {tipos.map((t) => <SelectOption key={t.id} value={t.id}>{t.nome}</SelectOption>)}
            </Select>
          </div>
          {/* 18/09: mesmo defeito do levantamento — 64 px mostravam "60" com 600 dentro. Cinco dígitos cabem. */}
          <div className="w-28">
            <Label>Qtd</Label>
            <Input size="sm" type="number" className="text-right tabular-nums" aria-label="Quantidade a comprar" value={qtd} onChange={(e) => setQtd(e.target.value)} onBlur={() => { const n = parseNumeroBr(qtd); if (n !== null && n >= 0 && n !== item.quantidade) patchItem({ quantidade: n }); }} />
          </div>
        </div>
        {/* 🔴 17/09 — UM número, UMA origem: com venda no Ganho, aqui é LEITURA (editar aqui fazia a margem discordar do vendido). */}
        <div className="text-right shrink-0">
          {item.vendaDoGanho ? (
            <>
              <Label>Vendido (R$)</Label>
              <p className="text-lg font-semibold tabular-nums text-ink-1 dark:text-ink-dark-1 leading-tight">{brl(item.vendaTotalCentavos)}</p>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3" data-testid={`vendido-${item.id}`}>
                {item.quantidadeVendida} un × {brl(item.vendaUnitCentavos ?? 0)} · do Ganho
                <HelpTip texto="O que o cliente comprou desse item, congelado no Ganho da proposta — é contra ele que a margem sai. Não se edita aqui: se a venda mudou, a mudança é na proposta. A quantidade ao lado é a do levantamento, o que se compra agora." className="ml-1" />
              </p>
            </>
          ) : (
            <>
              <Label>Venda informada (R$)</Label>
              <Input size="sm" type="number" aria-label="Venda total do item" value={venda} onChange={(e) => setVenda(e.target.value)} onBlur={() => { const c = toCent(venda); if (c !== item.vendaTotalCentavos) patchItem({ vendaTotalCentavos: c }); }} className="w-28 text-right" />
              <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">item fora da venda — informe</p>
            </>
          )}
        </div>
        <Button size="sm" variant="ghost" className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark shrink-0 min-h-[44px] min-w-[44px]" onClick={remover} aria-label="Remover item">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* 🔴 17/09 — DE ONDE SAI: parte do estoque, parte comprada (decisão do Matheus). O estoque é uma QUANTIDADE, não uma origem. */}
      <div className="flex flex-wrap items-end gap-3 mb-3" data-testid={`onde-comprou-${item.id}`}>
        <div className="w-28">
          <Label htmlFor={`qtd-estoque-${item.id}`}>
            Do estoque <HelpTip texto="Quantas unidades deste item já estão na empresa e não precisam ser compradas. O resto vai para a cotação. O sistema não sabe o seu estoque: a quantidade e o custo são informados por você." />
          </Label>
          <Input
            id={`qtd-estoque-${item.id}`}
            size="sm"
            type="number"
            min={0}
            aria-label="Quantidade que sai do estoque"
            value={qtdEstoque}
            onChange={(e) => { setQtdEstoque(e.target.value); if (erroCampo?.campo === 'qtdEstoque') setErroCampo(null); }}
            onBlur={() => { const n = parseNumeroBr(qtdEstoque); if (n !== null && n >= 0 && n !== (item.qtdEstoque ?? 0)) patchItem({ qtdEstoque: n }); }}
            aria-invalid={erroCampo?.campo === 'qtdEstoque' || undefined}
            data-testid={`qtd-estoque-${item.id}`}
          />
          <ErroCampo id={`qtd-estoque-${item.id}`} mensagem={erroCampo?.campo === 'qtdEstoque' ? erroCampo.mensagem : undefined} />
        </div>
        {parteDoEstoque && (
          <div className="w-40">
            <Label htmlFor={`custo-estoque-${item.id}`}>
              Custo do estoque (un.) <HelpTip texto="Quanto custou UMA unidade quando ela foi comprada. É esse valor que vai para a obra como custo da parte que saiu do estoque — sem ele a obra parece mais lucrativa do que é." />
            </Label>
            <Input
              id={`custo-estoque-${item.id}`}
              size="sm"
              type="number"
              placeholder="R$ por unidade"
              value={custoEstoque}
              onChange={(e) => setCustoEstoque(e.target.value)}
              onBlur={() => { const v = custoEstoque.trim(); const c = v ? toCent(v) : null; if (c !== item.custoEstoqueUnitCentavos) patchItem({ custoEstoqueUnitCentavos: c }); }}
              data-testid={`custo-estoque-${item.id}`}
            />
          </div>
        )}
        {temCompra && (
          <div className="w-full sm:w-64">
            <Label htmlFor={`onde-${item.id}`}>
              {parteDoEstoque ? `As ${item.qtdComprar} compradas vêm de` : 'Onde comprou'}{' '}
              <HelpTip texto="De onde vem a parte que ainda se compra. Cotação com fornecedor: você pede preço e compara abaixo. Marketplace ou loja de varejo: registra-se onde comprou e o valor pago; ninguém cota Mercado Livre. Fornecedor é só de quem se cota." />
            </Label>
            <Select id={`onde-${item.id}`} size="sm" value={ondeMostrado} onChange={(e) => mudarOndeComprou(e.target.value as OndeComprou)} aria-invalid={erroCampo?.campo === 'ondeComprou' || undefined}>
              {(['cotacao', 'marketplace', 'varejo'] as OndeComprou[]).map((k) => (
                <SelectOption key={k} value={k}>{ONDE_COMPROU_LABEL[k]}</SelectOption>
              ))}
            </Select>
            <ErroCampo id={`onde-${item.id}`} mensagem={erroCampo?.campo === 'ondeComprou' ? erroCampo.mensagem : undefined} />
          </div>
        )}
        {(ondeMostrado === 'marketplace' || ondeMostrado === 'varejo') && (
          <div className="w-full sm:w-56">
            <Label htmlFor={`onde-nome-${item.id}`}>{ondeMostrado === 'marketplace' ? 'Qual marketplace' : 'Qual loja'}</Label>
            <Input
              id={`onde-nome-${item.id}`}
              size="sm"
              value={ondeNome}
              placeholder={ondeMostrado === 'marketplace' ? 'ex.: Mercado Livre' : 'ex.: Eletro Almeida (balcão)'}
              onChange={(e) => { setOndeNome(e.target.value); if (erroCampo?.campo === 'ondeComprouNome') setErroCampo(null); }}
              onBlur={salvarOndeNome}
              onKeyDown={(e) => { if (e.key === 'Enter') salvarOndeNome(); }}
              aria-invalid={erroCampo?.campo === 'ondeComprouNome' || undefined}
              aria-describedby={erroCampo?.campo === 'ondeComprouNome' ? `onde-nome-${item.id}-erro` : undefined}
            />
            <ErroCampo id={`onde-nome-${item.id}`} mensagem={erroCampo?.campo === 'ondeComprouNome' ? erroCampo.mensagem : undefined} />
          </div>
        )}
        {fraseDaDivisao && (
          <Badge variant="neutral" size="sm" data-testid={`divisao-${item.id}`}>
            {fraseDaDivisao}
          </Badge>
        )}
        {!naCotacao && (
          <Badge variant="info" size="sm" data-testid={`pula-cotacao-${item.id}`}>
            {!temCompra ? 'Tudo sai do estoque — não se cota' : `Comprado em ${item.ondeComprouNome ?? '—'} — não se cota`}
          </Badge>
        )}
      </div>

      {/* Camada 2 — Comparação: preço E margem lado a lado, as duas pontas marcadas. Só na cotação. */}
      {naCotacao && (
        <div data-testid={`comparacao-${item.id}`}>
          {fornecedores.length === 0 ? (
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
              {item.tipoItemId
                ? 'Nenhum fornecedor cadastrado nessa família ainda — acrescente abaixo quem você vai chamar.'
                : 'Defina a família do item para ver os fornecedores sugeridos, ou acrescente abaixo quem você vai chamar.'}
            </p>
          ) : (
            <>
              {item.pontasDivergem && nomeBarato && nomeMelhor && (
                <Alert variant="warning" className="mb-2" data-testid={`pontas-divergem-${item.id}`}>
                  <strong>O mais barato não é o de melhor margem.</strong> {nomeBarato} tem o menor preço de lista; {nomeMelhor} fecha melhor depois do desconto. É o caso de olhar os dois antes de escolher.
                </Alert>
              )}
              {/* Desktop: a tabela ordenável. */}
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 border-b border-line dark:border-line-dark">
                      <ThOrdenavel chave="fornecedor" ordem={ordem} className="text-left font-normal py-1">Fornecedor</ThOrdenavel>
                      <ThOrdenavel chave="preco" ordem={ordem} className="text-right font-normal">
                        Preço un.
                      </ThOrdenavel>
                      <th className="text-center font-normal w-20">% desc <HelpTip texto="O desconto que você conseguiu DEPOIS da cotação. O preço original fica guardado: a diferença entre os dois é o seu ganho de negociação." /></th>
                      <th className="text-right font-normal">Total</th>
                      <th className="text-right font-normal">Total c/ desc</th>
                      <ThOrdenavel chave="margem" ordem={ordem} className="text-right font-normal">
                        Margem
                      </ThOrdenavel>
                      <th className="text-right font-normal w-16">%</th>
                      <th className="w-28" />
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((f) => (
                      <BlocoRow
                        key={f.id}
                        cotacaoId={cotacaoId}
                        itemId={item.id}
                        fornecedor={f}
                        bloco={blocoByForn.get(f.id) ?? null}
                        vendaTotalCentavos={item.vendaTotalCentavos}
                        isMelhor={f.id === melhorFornId}
                        isBarato={f.id === baratoFornId}
                        isEscolhido={f.id === item.fornecedorEscolhidoId}
                        onEscolher={() => patchItem({ fornecedorEscolhidoId: f.id === item.fornecedorEscolhidoId ? null : f.id })}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Celular: um cartão por fornecedor (tabela larga vira cartão, nunca rolagem de lado). */}
              <div className="md:hidden flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs text-ink-3 dark:text-ink-dark-3">
                  <span>Ordenar por</span>
                  <Button size="sm" variant={ordem.chave === 'preco' ? 'primary' : 'secondary'} className="min-h-[44px]" onClick={() => ordem.alternar('preco')}>preço</Button>
                  <Button size="sm" variant={ordem.chave === 'margem' ? 'primary' : 'secondary'} className="min-h-[44px]" onClick={() => ordem.alternar('margem')}>margem</Button>
                </div>
                {linhas.map((f) => (
                  <BlocoCard
                    key={f.id}
                    cotacaoId={cotacaoId}
                    itemId={item.id}
                    fornecedor={f}
                    bloco={blocoByForn.get(f.id) ?? null}
                    vendaTotalCentavos={item.vendaTotalCentavos}
                    isMelhor={f.id === melhorFornId}
                    isBarato={f.id === baratoFornId}
                    isEscolhido={f.id === item.fornecedorEscolhidoId}
                    onEscolher={() => patchItem({ fornecedorEscolhidoId: f.id === item.fornecedorEscolhidoId ? null : f.id })}
                  />
                ))}
              </div>
            </>
          )}
          {/* Acrescentar fornecedor fora da sugestão — cotação parcial é normal, e a sugestão pode estar vazia. */}
          <div className="mt-2 flex flex-wrap items-end gap-2" data-testid={`acrescentar-fornecedor-${item.id}`}>
            <div className="w-full sm:w-72">
              <Label htmlFor={`acrescentar-${item.id}`}>
                Acrescentar fornecedor <HelpTip texto="Qualquer cadastro com a etiqueta Fornecedor, mesmo fora da família. A linha aparece vazia: digite o preço que ele passou e ela entra na comparação. Fornecedor novo se cadastra em Clientes, com a etiqueta." />
              </Label>
              <BuscaCombobox
                id={`acrescentar-${item.id}`}
                value={acrescentarId}
                onChange={(fid) => {
                  setAcrescentarId('');
                  const f = paraAcrescentar.find((x) => x.id === fid);
                  if (f) setExtras((prev) => (prev.some((x) => x.id === f.id) ? prev : [...prev, f]));
                }}
                items={paraAcrescentar.map((f) => ({
                  id: f.id,
                  // 17/09: com parentes, o lugar entra no rótulo — senão as três SUPRITEC são a mesma linha.
                  label: (f.parentes ?? []).length > 0 ? nomeComLugar(f.nome, f.lugar) : f.nome,
                  // Fase 2: a razão social entra na busca e no rótulo — a fantasia sozinha não distingue
                  // duas empresas com a mesma marca; e o parentesco aparece antes de escolher.
                  sublabel: [
                    (f.razaoSocial ?? '').trim() && (f.razaoSocial ?? '').trim().toLowerCase() !== f.nome.trim().toLowerCase() ? f.razaoSocial : null,
                    (f.parentes ?? []).length > 0 ? fraseDosParentes(f.nome, f.parentes ?? []) : null,
                  ].filter(Boolean).join(' · ') || undefined,
                }))}
                placeholder="Buscar fornecedor pelo nome…"
                emptyText="Nenhum fornecedor com esse nome"
              />
            </div>
          </div>
        </div>
      )}

      {/* Camada 3 — Faixa de fechamento: fechado · quem/quando · ganho · recebido × fechado · margem final. */}
      <div className="mt-3 pt-3 border-t border-line dark:border-line-dark flex flex-wrap items-end gap-4" data-testid={`fechamento-${item.id}`}>
        {temCompra && (
        <div className="w-full sm:w-44">
          <Label>
            {naCotacao ? 'Valor fechado da compra (manual)' : 'Valor pago (R$)'}{' '}
            <HelpTip texto={naCotacao
              ? 'Use só quando o valor fechado não vem de uma linha de fornecedor. Se um fornecedor está escolhido, o fechado é o total com desconto dele.'
              : 'Quanto esse material custou: o valor de compra do estoque ou o que foi pago no marketplace/loja. É contra ele que a margem sai.'} />
          </Label>
          <Input
            size="sm"
            type="number"
            aria-label={naCotacao ? 'Valor fechado manual' : 'Valor pago'}
            defaultValue={item.valorCompradoCentavos != null ? String(item.valorCompradoCentavos / 100) : ''}
            placeholder={naCotacao ? 'fora de linha' : 'R$'}
            onBlur={(e) => { const v = e.target.value.trim(); patchItem({ valorCompradoCentavos: v ? toCent(v) : null }); }}
          />
        </div>
        )}
        <div className="text-sm leading-relaxed">
          <div className="text-ink-2 dark:text-ink-dark-2">
            {/* 🔴 17/09 (régua do #311): sem dado, a tela DIZ o que falta — nunca só um traço. */}
            Fechado:{' '}
            <span className="tabular-nums font-medium text-ink-1 dark:text-ink-dark-1" data-testid={`fechado-${item.id}`}>
              {item.custoTotalCentavos != null ? brl(item.custoTotalCentavos) : (item.fechadoFalta ?? '—')}
            </span>
            {item.custoTotalCentavos != null && parteDoEstoque && temCompra ? (
              <span className="text-xs text-ink-3 dark:text-ink-dark-3 ml-1" data-testid={`fechado-partes-${item.id}`}>
                ({item.qtdEstoque} do estoque {brl(item.custoEstoqueCentavos ?? 0)} · {item.qtdComprar} da compra {brl(item.valorCompradoEfetivoCentavos ?? 0)})
              </span>
            ) : item.fornecedorEscolhidoId && escolhidoBloco
              ? <span className="text-xs text-ink-3 dark:text-ink-dark-3 ml-1">(com {fornecedorNome.get(item.fornecedorEscolhidoId) ?? 'o fornecedor escolhido'})</span>
              : item.custoTotalCentavos != null && !parteDoEstoque
                ? <span className="text-xs text-ink-3 dark:text-ink-dark-3 ml-1">({naCotacao ? 'manual' : ONDE_COMPROU_LABEL[item.ondeComprou]})</span>
                : item.custoTotalCentavos != null && !temCompra
                  ? <span className="text-xs text-ink-3 dark:text-ink-dark-3 ml-1">(do estoque)</span>
                  : null}
          </div>
          {item.fornecedorEscolhidoId && (
            <div className="text-xs text-ink-3 dark:text-ink-dark-3" data-testid={`escolhido-por-${item.id}`}>
              escolhido por {item.fornecedorEscolhidoPorNome ?? '—'} em {dataHoraBR(item.fornecedorEscolhidoEmIso)}
            </div>
          )}
          {item.ganhoNegociacaoCentavos != null && item.economiaPct != null && (
            <div className="text-ink-2 dark:text-ink-dark-2" data-testid={`ganho-${item.id}`}>
              Ganho de negociação:{' '}
              <span className={`tabular-nums font-medium ${item.ganhoNegociacaoCentavos < 0 ? 'text-red' : 'text-ink-1 dark:text-ink-dark-1'}`}>{brl(item.ganhoNegociacaoCentavos)} ({pct(item.economiaPct)} de economia)</span>
              <HelpTip texto="Preço de lista do fornecedor escolhido menos o que fechou depois do desconto. É o indicador do trabalho de negociação — calculado, ninguém digita." className="ml-1" />
            </div>
          )}
          <div className="text-ink-2 dark:text-ink-dark-2">
            Margem final:{' '}
            <span className={`tabular-nums font-medium ${item.margemFinalCentavos != null && item.margemFinalCentavos < 0 ? 'text-red' : 'text-ink-1 dark:text-ink-dark-1'}`} data-testid={`margem-final-${item.id}`}>
              {item.margemFinalCentavos != null ? `${brl(item.margemFinalCentavos)} (${pct(item.margemFinalPct!)})` : (item.margemFalta ?? '—')}
            </span>
          </div>
        </div>
        {/* Fase 2 — o recebido (a nota) ao lado do fechado, com a diferença visível. 17/09: a nota é da COMPRA. */}
        {temCompra && (
        <div className="w-full sm:w-52" data-testid={`recebido-${item.id}`}>
          <Label htmlFor={`recebido-${item.id}`}>
            Recebido na nota (R$) <HelpTip texto="O valor que veio na nota fiscal. O fechado não muda: os dois ficam guardados e a diferença aparece ao lado — é o dinheiro que costuma sumir sem ninguém ver." />
          </Label>
          <Input
            id={`recebido-${item.id}`}
            size="sm"
            type="number"
            value={recebido}
            placeholder="quando a nota chegar"
            onChange={(e) => setRecebido(e.target.value)}
            onBlur={() => {
              const v = recebido.trim();
              const c = v ? toCent(v) : null;
              if (c !== item.custoRecebidoCentavos) patchItem({ custoRecebidoCentavos: c });
            }}
          />
          {item.diferencaRecebidoCentavos != null && (
            <p className={`text-xs mt-1 tabular-nums ${item.diferencaRecebidoCentavos > 0 ? 'text-red' : item.diferencaRecebidoCentavos < 0 ? 'text-success dark:text-[#9FC791]' : 'text-ink-3 dark:text-ink-dark-3'}`} data-testid={`diferenca-${item.id}`}>
              {item.diferencaRecebidoCentavos === 0 ? 'igual ao fechado' : `${brlComSinal(item.diferencaRecebidoCentavos)} em relação ao fechado`}
            </p>
          )}
        </div>
        )}
        {/* 17/09: a etiqueta "A caminho" MORREU daqui — dizia o mesmo que o resumo da Logística, e num item de estoque
            afirmava uma entrega que nunca foi comprada. O estado mora num lugar só: o resumo da Logística. */}
      </div>


      {/* 🔴 17/09 — FECHAR A COMPRA, à vista (decisão do Cowork): liberar e efetivar decidem dinheiro e moravam dentro de
          "Realizado do projeto", que abre FECHADO — ninguém procura o fim do fluxo atrás de um acordeão. A categoria vem junto
          porque sem ela a liberação é recusada. */}
      <div className="mt-3 pt-3 border-t border-line dark:border-line-dark flex flex-wrap items-end gap-3" data-testid={`fechar-compra-${item.id}`}>
        <div className="w-40">
          <Label>Categoria do custo</Label>
          <Select
            size="sm"
            aria-label="Categoria do custo"
            value={item.realizadoCategoria ?? ''}
            onChange={(e) => patchItem({ realizadoCategoria: (e.target.value || null) as RealizadoCategoria | null })}
          >
            <SelectOption value="">— escolher —</SelectOption>
            {(Object.keys(CATEGORIA_REALIZADO_LABEL) as RealizadoCategoria[]).map((c) => (
              <SelectOption key={c} value={c}>{CATEGORIA_REALIZADO_LABEL[c]}</SelectOption>
            ))}
          </Select>
        </div>
        <div className="w-32">
          <Label>Tipo</Label>
          <Select
            size="sm"
            aria-label="Previsto ou imprevisto"
            value={item.realizadoClassificacao ?? ''}
            onChange={(e) => patchItem({ realizadoClassificacao: (e.target.value || null) as 'previsto' | 'imprevisto' | null })}
          >
            <SelectOption value="">—</SelectOption>
            <SelectOption value="previsto">Previsto</SelectOption>
            <SelectOption value="imprevisto">Imprevisto</SelectOption>
          </Select>
        </div>
        {!item.realizadoAprovacaoStatus && !item.compraEfetivada && (
          <Button size="sm" className="min-h-[44px]" onClick={lancarRealizado} disabled={actions.lancarRealizado.isPending} data-testid={`liberar-${item.id}`}>
            {actions.lancarRealizado.isPending ? 'Liberando…' : 'Liberar compra'}
          </Button>
        )}
        {item.compraEfetivada ? (
          <p className="text-xs text-success dark:text-[#9FC791] flex items-center gap-1">
            <Check className="w-3.5 h-3.5" /> Compra efetivada — custo lançado no realizado do projeto
            {parteDoEstoque && temCompra ? ' (dois lançamentos: da compra e do estoque)' : parteDoEstoque ? ' (do estoque)' : ''}.
          </p>
        ) : item.realizadoAprovacaoStatus === 'aprovado' ? (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs text-info dark:text-[#9CC2DC]">✓ Liberada — falta efetivar a compra.</p>
            <Button
              size="sm"
              className="min-h-[44px]"
              onClick={efetivarCompra}
              disabled={actions.efetivar.isPending || (!!item.fornecedorEscolhidoId && !escolhidoBloco?.numeroPedidoNf)}
              title={!!item.fornecedorEscolhidoId && !escolhidoBloco?.numeroPedidoNf ? 'Preencha o Nº do Pedido/NF do fornecedor escolhido antes de efetivar.' : undefined}
              data-testid={`efetivar-${item.id}`}
            >
              {actions.efetivar.isPending ? 'Efetivando…' : 'Marcar compra efetivada'}
            </Button>
            {!!item.fornecedorEscolhidoId && !escolhidoBloco?.numeroPedidoNf && (
              <p className="text-xs text-warning dark:text-[#D9AE6B]">Informe o Nº do Pedido/NF (bloco Pagamento) para efetivar.</p>
            )}
          </div>
        ) : null}
      </div>

      {/* Seções avançadas — colapsadas por padrão (resumo na linha fechada). */}
      {/* FX-27: bloco único "Pagamento" — forma de pagamento no topo + fat. direto inline. */}
      {item.fornecedorEscolhidoId && (
        <CollapsibleSection title="Pagamento" summary={pagamentoResumo} testId={`pagamento-${item.id}`}>
          {/* FX-24: forma de pagamento (catálogo) — escolhida com o fornecedor, viaja ao Financeiro. */}
          <div className="w-64 mb-4" data-testid={`forma-pagamento-${item.id}`}>
            <Label>Forma de pagamento</Label>
            <Select
              size="sm"
              aria-label="Forma de pagamento"
              value={item.formaPagamentoId ?? ''}
              onChange={(e) => patchItem({ formaPagamentoId: e.target.value || null })}
            >
              <SelectOption value="">— não informada —</SelectOption>
              {(formasPagamento.data?.items ?? [])
                .filter((f) => f.ativo || f.id === item.formaPagamentoId)
                .map((f) => (
                  <SelectOption key={f.id} value={f.id}>
                    {f.nome}
                  </SelectOption>
                ))}
            </Select>
          </div>

          {/* FX-36: Nº Pedido/NF do fornecedor escolhido — obrigatório pra efetivar. */}
          <div className="w-64 mb-4" data-testid={`pedido-nf-${item.id}`}>
            <Label>Nº do Pedido / NF</Label>
            <Input
              size="sm"
              aria-label="Número do pedido ou NF"
              value={pedidoNfInput}
              placeholder="ex.: 12345"
              onChange={(e) => setPedidoNfInput(e.target.value)}
              onBlur={() => {
                if (!item.fornecedorEscolhidoId) return;
                const v = pedidoNfInput.trim();
                if (v === (escolhidoBloco?.numeroPedidoNf ?? '')) return; // sem mudança
                actions.setPedidoNf.mutate(
                  { itemId: item.id, fornecedorId: item.fornecedorEscolhidoId, numeroPedidoNf: v || null },
                  { onError: (err) => toast.show(errMsg(err) ?? 'Não deu pra salvar o Nº do Pedido/NF.', { variant: 'error' }) },
                );
              }}
            />
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">Necessário para marcar a compra como efetivada.</p>
          </div>

          {/* Faturamento direto — sub-bloco inline (não accordion aninhado). */}
          <p className="text-xs font-medium text-ink-2 dark:text-ink-dark-2 mb-2">
            Faturamento direto <HelpTip texto="O fornecedor emite a nota direto para o cliente. A IGM recebe a margem (venda − preço do fornecedor) menos o imposto do distribuidor, que está no cadastro do fornecedor (aba Fornecedor, % de imposto). Essa margem líquida entra na margem da obra, marcada como vinda do faturamento direto, não do Omie." />
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Switch
              size="sm"
              label="Faturamento direto"
              id={`fat-${item.id}`}
              aria-invalid={erroCampo?.campo === 'faturamentoDireto' || undefined}
              aria-describedby={erroCampo?.campo === 'faturamentoDireto' ? `fat-${item.id}-erro` : undefined}
              checked={fatDiretoAberto}
              onChange={(e) => {
                if (e.target.checked) {
                  // ligar: se já tem empresa, ativa de fato; senão só revela o select.
                  if (item.empresaFaturamentoId) patchItem({ faturamentoDireto: true });
                  else setFatPendente(true);
                } else {
                  setFatPendente(false);
                  if (item.faturamentoDireto) patchItem({ faturamentoDireto: false });
                }
              }}
            />
            <ErroCampo id={`fat-${item.id}`} mensagem={erroCampo?.campo === 'faturamentoDireto' ? erroCampo.mensagem : undefined} />
            {item.faturamentoDiretoMotivo === 'abaixo-do-minimo' && item.faturamentoDiretoMinimoCentavos !== null && (
              /* 16/09 (decisão do Matheus): continua só AVISO — o Compras negocia e o fornecedor abre exceção —, mas com
                 os DOIS valores, em frase; nunca selo genérico. */
              <Alert variant="warning" className="basis-full" data-testid={`fat-abaixo-minimo-${item.id}`}>
                <strong>{brl(item.vendaTotalCentavos)} está abaixo do mínimo de {brl(item.faturamentoDiretoMinimoCentavos)} deste fornecedor.</strong>{' '}
                É aviso, não trava: se o fornecedor abriu exceção, pode seguir.
              </Alert>
            )}
            {item.faturamentoDiretoMotivo === 'minimo-nao-preenchido' && !item.fatDiretoAntesDaRegraDoMinimo && (
              <span className="inline-flex items-center gap-1" data-testid={`fat-sem-minimo-${item.id}`}>
                <Badge variant="warning">Mínimo de faturamento não preenchido no cadastro</Badge>
                <HelpTip texto="Sem o mínimo de faturamento no cadastro do fornecedor (ficha do cliente, aba Fornecedor), o faturamento direto não pode ser ligado. Vazio não quer dizer 'sem mínimo' — quer dizer que ninguém preencheu." />
              </span>
            )}
            {item.fatDiretoAntesDaRegraDoMinimo && item.faturamentoDireto && (
              <span className="inline-flex items-center gap-1" data-testid={`fat-herdado-${item.id}`}>
                <Badge variant="neutral">Ligado antes da regra do mínimo</Badge>
                <HelpTip texto="Este item já estava com faturamento direto ligado quando nasceu a regra 'sem mínimo preenchido, não liga' (16/09/2026). Ele continua como está. Se for desligado, ou trocar o fornecedor, ao ligar de novo vale a regra nova." />
              </span>
            )}
            {fatDiretoAberto && (
              <div className="w-56">
                <Label>Empresa que fatura (NF)</Label>
                <Select
                  size="sm"
                  aria-label="Empresa que fatura"
                  value={item.empresaFaturamentoId ?? ''}
                  onChange={(e) => {
                    const empresaId = e.target.value || null;
                    // Escolher a empresa ATIVA o faturamento direto (PATCH conjunto)
                    // — evita o erro "exige fornecedor + empresa" do clique solto.
                    if (empresaId && !item.faturamentoDireto) {
                      patchItem({ faturamentoDireto: true, empresaFaturamentoId: empresaId });
                      setFatPendente(false);
                    } else {
                      patchItem({ empresaFaturamentoId: empresaId });
                    }
                  }}
                >
                  <SelectOption value="">— escolha a empresa —</SelectOption>
                  {(empresas.data?.items ?? []).map((emp) => (
                    <SelectOption key={emp.id} value={emp.id}>
                      {empresaLabel(emp, { comAliquota: true })}
                    </SelectOption>
                  ))}
                </Select>
              </div>
            )}
          </div>
          {fatPendente && !item.empresaFaturamentoId && (
            <p className="mt-2 text-xs text-warning dark:text-[#D9AE6B]">
              Escolha a <strong>empresa que fatura (NF)</strong> para ativar o faturamento direto.
            </p>
          )}
          {item.faturamentoDireto && item.faturamento && !item.faturamento.impostoDistribuidorPreenchido && (
            /* Fase 2 (decisão do Matheus): sem o % do distribuidor no cadastro a conta NÃO se faz — a tela diz. */
            <Alert variant="warning" className="mt-2" data-testid={`imposto-distribuidor-vazio-${item.id}`}>
              <strong>Imposto do distribuidor não preenchido</strong> no cadastro de {item.fornecedorEscolhidoId ? fornecedorNome.get(item.fornecedorEscolhidoId) ?? 'fornecedor' : 'fornecedor'} (ficha do cliente, aba Fornecedor, campo &ldquo;% de imposto&rdquo;). Sem ele a margem líquida fica incompleta e não entra na obra.
            </Alert>
          )}
          {item.faturamentoDireto && item.faturamento && item.faturamento.impostoDistribuidorPreenchido && (
            /* Fase 2 (16/09/2026): SÃO DOIS IMPOSTOS. Com o crédito retido, só a NF do fornecedor;
               se a IGM retirar, mais a NF de serviço dela — e só para quem cobra. Ninguém calcula na cabeça. */
            <div className="mt-2 grid gap-2 sm:grid-cols-2" data-testid={`faturamento-direto-conta-${item.id}`}>
              <div className="rounded-lg border border-line dark:border-line-dark p-2">
                <p className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                  Deixando o crédito com o fornecedor
                  <HelpTip texto="O fornecedor emite a nota dele e guarda o seu crédito. Você paga só o imposto da NF dele, que está no cadastro (aba Fornecedor, % de imposto). É este o valor que entra na margem da obra." className="ml-1" />
                </p>
                <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                  Imposto do fornecedor: <span className="tabular-nums">{brl(item.faturamento.impostoDistribuidorCentavos ?? 0)}</span>
                </p>
                <p className="text-base font-semibold tabular-nums text-ink-1 dark:text-ink-dark-1" data-testid={`margem-retida-${item.id}`}>
                  {brl(item.faturamento.margemComCreditoRetidoCentavos ?? 0)}
                </p>
              </div>
              <div className="rounded-lg border border-line dark:border-line-dark p-2" data-testid={`se-retirar-${item.id}`}>
                <p className="text-xs uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                  Se retirar o crédito
                  <HelpTip texto="Para tirar o crédito do fornecedor, a IGM emite uma nota de serviço e paga a alíquota de NF de SERVIÇO da empresa que fatura (Configurações do Compras). Só os fornecedores marcados como 'cobra imposto para retirada' cobram isso; nos outros, retirar não custa nada a mais." className="ml-1" />
                </p>
                {item.faturamento.cobraNaRetirada && item.faturamento.aliquotaRetiradaPct === null ? (
                  <p className="text-sm text-warning dark:text-[#D9AE6B]" data-testid={`retirada-sem-aliquota-${item.id}`}>
                    A empresa que fatura não tem alíquota de NF de serviço configurada (Configurações do Compras) — a conta da retirada não sai.
                  </p>
                ) : item.faturamento.cobraNaRetirada ? (
                  <>
                    <p className="text-sm text-ink-2 dark:text-ink-dark-2" data-testid={`aliquota-retirada-${item.id}`}>
                      NF de serviço {(item.faturamento.aliquotaRetiradaPct ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%:{' '}
                      <span className="tabular-nums">−{brl(item.faturamento.impostoServicoRetiradaCentavos ?? 0)}</span>
                    </p>
                    <p className="text-base font-semibold tabular-nums text-ink-1 dark:text-ink-dark-1">
                      sobram {brl(item.faturamento.margemSeRetirarCentavos ?? 0)}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                    Este fornecedor não cobra para a retirada — sobram os mesmos{' '}
                    <span className="tabular-nums font-medium text-ink-1 dark:text-ink-dark-1">{brl(item.faturamento.margemSeRetirarCentavos ?? 0)}</span>.
                  </p>
                )}
              </div>
            </div>
          )}
          {item.faturamentoDireto && !item.faturamento && (
            <p className="mt-2 text-xs text-ink-3 dark:text-ink-dark-3">
              Feche o <strong>valor comprado</strong> (escolha o fornecedor ou informe o valor manual) pra calcular os impostos sobre a margem.
            </p>
          )}
          {/* FX-28: gera crédito no fornecedor — caso a caso, só no fat. direto. */}
          {item.faturamentoDireto && (
            <div className="mt-3" data-testid={`gera-credito-${item.id}`}>
              <Switch
                size="sm"
                label="Gera crédito no fornecedor"
                checked={item.geraCredito}
                onChange={(e) => patchItem({ geraCredito: e.target.checked })}
              />
              {item.geraCredito && (
                <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                  Ao lançar no realizado, a margem líquida (≥ 0) vira crédito do fornecedor escolhido.
                </p>
              )}
            </div>
          )}
        </CollapsibleSection>
      )}

      <CollapsibleSection title="Realizado do projeto" summary={realizadoResumo} testId={`realizado-${item.id}`}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label>Item da solicitação</Label>
            <Select
              size="sm"
              aria-label="Item da solicitação"
              value={item.solicitacaoItemId ?? ''}
              onChange={(e) => patchItem({ solicitacaoItemId: e.target.value || null })}
            >
              <SelectOption value="">— avulso (não lança) —</SelectOption>
              {(solItens.data?.items ?? []).map((si) => (
                <SelectOption key={si.id} value={si.id}>
                  {si.item} · {si.usoInterno ? 'uso interno' : si.obra || si.solicitanteNome}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Logística" summary={logisticaResumo} testId={`logistica-${item.id}`}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Label>Previsão de chegada</Label>
            <Input
              size="sm"
              type="date"
              aria-label="Previsão de chegada"
              defaultValue={item.previsaoChegada ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim() || null;
                if (v !== item.previsaoChegada) patchItem({ previsaoChegada: v });
              }}
            />
          </div>
          {/* SU-1: etapas finais do status canônico (movem o card no quadro). */}
          <Switch
            size="sm"
            label="Recebido (na empresa)"
            checked={item.recebido}
            onChange={(e) => patchItem({ recebido: e.target.checked })}
          />
          <Switch
            size="sm"
            label="Entregue (destino)"
            checked={item.entregue}
            onChange={(e) => patchItem({ entregue: e.target.checked })}
          />
        </div>
      </CollapsibleSection>
    </LinhaRecolhivel>
  );
}

// ─── A linha do fornecedor (o mesmo estado nas duas casas: tabela e cartão) ──
type BlocoProps = {
  cotacaoId: string;
  itemId: string;
  fornecedor: FornecedorLite;
  bloco: BlocoComputed | null;
  vendaTotalCentavos: number;
  isMelhor: boolean;
  isBarato: boolean;
  isEscolhido: boolean;
  onEscolher: () => void;
};

/**
 * O nome do fornecedor na cotação — um lugar só, usado na tabela (desktop) e no cartão (celular).
 *
 * Fase 2 (decisões do Matheus, 15/09/2026):
 *   · **razão social junto da fantasia**, nunca a fantasia sozinha: duas empresas diferentes
 *     usam a marca "INNOVATIE", e só a razão social as separa;
 *   · **"parente de X"** quando outro cadastro tem a MESMA RAIZ de CNPJ (matriz/filial) — para
 *     quem cota não achar que pediu a dois quando pediu ao mesmo grupo. É informativo: os dois
 *     cotam separado, com preço e prazo próprios;
 *   · **sem CNPJ válido, nenhum selo** — a tela não diz "sem parentes", porque não sabe.
 */
function NomeDoFornecedor({ fornecedor }: { fornecedor: FornecedorLite }) {
  const razao = (fornecedor.razaoSocial ?? '').trim();
  const mostraRazao = razao && razao.toLowerCase() !== fornecedor.nome.trim().toLowerCase();
  const parentes = fornecedor.parentes ?? [];
  return (
    <>
      <span className="text-ink-1 dark:text-ink-dark-1" data-testid={`nome-${fornecedor.id}`}>
        {parentes.length > 0 ? nomeComLugar(fornecedor.nome, fornecedor.lugar) : fornecedor.nome}
      </span>
      {mostraRazao && (
        <span className="block text-xs text-ink-3 dark:text-ink-dark-3" data-testid={`razao-${fornecedor.id}`}>
          {razao}
        </span>
      )}
      {parentes.length > 0 && (
        <span className="block text-xs text-ink-2 dark:text-ink-dark-2" data-testid={`parente-${fornecedor.id}`}>
          {fraseDosParentes(fornecedor.nome, parentes)}
          <HelpTip
            texto="Os dois cadastros são da mesma empresa (o CNPJ tem a mesma raiz): um é a matriz, o outro é filial. Cada um cota por conta própria, com preço e prazo próprios — o aviso só existe para você não achar que pediu a dois fornecedores quando pediu ao mesmo grupo."
            className="ml-1"
          />
        </span>
      )}
    </>
  );
}

function useBlocoInputs({ cotacaoId, itemId, fornecedor, bloco }: BlocoProps) {
  const actions = useCotacaoActions(cotacaoId);
  const toast = useToast();
  const [cot, setCot] = useState(bloco ? String(bloco.cotacaoUnitCentavos / 100) : '');
  const [desc, setDesc] = useState(bloco ? String(bloco.descontoPct) : '');
  // CO-4: re-sincroniza quando o bloco muda por fora (outro save recalcula o full).
  useEffect(() => {
    setCot(bloco ? String(bloco.cotacaoUnitCentavos / 100) : '');
    setDesc(bloco ? String(bloco.descontoPct) : '');
  }, [bloco]);

  function salvar() {
    const cotacaoUnitCentavos = toCent(cot);
    const descontoPct = parseNumeroBr(desc) ?? 0;
    if (!cot && !bloco) return; // nada digitado e sem bloco — não cria vazio
    if (bloco && cotacaoUnitCentavos === bloco.cotacaoUnitCentavos && descontoPct === bloco.descontoPct) return;
    actions.setBloco.mutate(
      { itemId, fornecedorId: fornecedor.id, patch: { cotacaoUnitCentavos, descontoPct } },
      { onError: (e) => toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', { variant: 'error' }) },
    );
  }
  return { cot, setCot, desc, setDesc, salvar };
}

function margemPct(bloco: BlocoComputed | null, venda: number): string {
  if (!bloco || !bloco.cotacaoUnitCentavos || venda <= 0) return '—';
  return pct((bloco.margemComDescontoCentavos / venda) * 100);
}

function BlocoRow(props: BlocoProps) {
  const { fornecedor, bloco, vendaTotalCentavos, isMelhor, isBarato, isEscolhido, onEscolher } = props;
  const { cot, setCot, desc, setDesc, salvar } = useBlocoInputs(props);
  const temPreco = !!bloco && bloco.cotacaoUnitCentavos > 0;

  return (
    <tr className={`border-b border-line dark:border-line-dark last:border-b-0 ${isEscolhido ? 'bg-red-soft/30 dark:bg-red-soft-dark/30' : ''}`} data-testid={`bloco-${fornecedor.id}`}>
      <td className="py-1.5 pr-2">
        <NomeDoFornecedor fornecedor={fornecedor} />
        {isBarato && <Badge variant="info" size="sm" className="ml-2">mais barato</Badge>}
        {isMelhor && <Badge variant="success" size="sm" className="ml-2">melhor margem</Badge>}
      </td>
      <td className="text-right">
        <Input size="sm" type="number" aria-label={`Preço unitário de ${fornecedor.nome}`} value={cot} onChange={(e) => setCot(e.target.value)} onBlur={salvar} className="w-24 text-right" placeholder="R$" />
      </td>
      <td className="text-center">
        <Input size="sm" type="number" aria-label={`Desconto de ${fornecedor.nome}`} value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={salvar} className="w-16 text-center" placeholder="0" />
      </td>
      <td className="text-right tabular-nums whitespace-nowrap text-ink-2 dark:text-ink-dark-2">{temPreco ? brl(bloco!.totalCentavos) : '—'}</td>
      <td className="text-right tabular-nums whitespace-nowrap text-ink-1 dark:text-ink-dark-1">{temPreco ? brl(bloco!.totalComDescontoCentavos) : '—'}</td>
      <td className={`text-right tabular-nums whitespace-nowrap font-medium ${temPreco && bloco!.margemComDescontoCentavos < 0 ? 'text-red' : 'text-ink-1 dark:text-ink-dark-1'}`}>{temPreco ? brl(bloco!.margemComDescontoCentavos) : '—'}</td>
      <td className="text-right tabular-nums whitespace-nowrap text-ink-2 dark:text-ink-dark-2">{margemPct(bloco, vendaTotalCentavos)}</td>
      <td className="text-right">
        <Button size="sm" variant={isEscolhido ? 'primary' : 'ghost'} onClick={onEscolher} disabled={!temPreco && !isEscolhido} title={!temPreco && !isEscolhido ? 'Digite o preço antes de escolher.' : undefined}>
          {isEscolhido ? (
            <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" />Escolhido</span>
          ) : (
            'Escolher'
          )}
        </Button>
      </td>
    </tr>
  );
}

function BlocoCard(props: BlocoProps) {
  const { fornecedor, bloco, vendaTotalCentavos, isMelhor, isBarato, isEscolhido, onEscolher } = props;
  const { cot, setCot, desc, setDesc, salvar } = useBlocoInputs(props);
  const temPreco = !!bloco && bloco.cotacaoUnitCentavos > 0;

  return (
    <div className={`rounded-lg border border-line dark:border-line-dark p-3 ${isEscolhido ? 'bg-red-soft/30 dark:bg-red-soft-dark/30' : ''}`} data-testid={`bloco-card-${fornecedor.id}`}>
      <div className="mb-2">
        <div className="text-sm font-medium">
          <NomeDoFornecedor fornecedor={fornecedor} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {isBarato && <Badge variant="info" size="sm">mais barato</Badge>}
          {isMelhor && <Badge variant="success" size="sm">melhor margem</Badge>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label htmlFor={`cot-${props.itemId}-${fornecedor.id}`}>Preço un. (R$)</Label>
          <Input id={`cot-${props.itemId}-${fornecedor.id}`} size="sm" type="number" value={cot} onChange={(e) => setCot(e.target.value)} onBlur={salvar} placeholder="R$" className="min-h-[44px]" />
        </div>
        <div>
          <Label htmlFor={`desc-${props.itemId}-${fornecedor.id}`}>% desc</Label>
          <Input id={`desc-${props.itemId}-${fornecedor.id}`} size="sm" type="number" value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={salvar} placeholder="0" className="min-h-[44px]" />
        </div>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-sm">
        <dt className="text-ink-3 dark:text-ink-dark-3">Total</dt>
        <dd className="text-right tabular-nums text-ink-2 dark:text-ink-dark-2">{temPreco ? brl(bloco!.totalCentavos) : '—'}</dd>
        <dt className="text-ink-3 dark:text-ink-dark-3">Total c/ desc</dt>
        <dd className="text-right tabular-nums text-ink-1 dark:text-ink-dark-1">{temPreco ? brl(bloco!.totalComDescontoCentavos) : '—'}</dd>
        <dt className="text-ink-3 dark:text-ink-dark-3">Margem</dt>
        <dd className={`text-right tabular-nums font-medium ${temPreco && bloco!.margemComDescontoCentavos < 0 ? 'text-red' : 'text-ink-1 dark:text-ink-dark-1'}`}>
          {temPreco ? `${brl(bloco!.margemComDescontoCentavos)} (${margemPct(bloco, vendaTotalCentavos)})` : '—'}
        </dd>
      </dl>
      <Button size="sm" variant={isEscolhido ? 'primary' : 'secondary'} onClick={onEscolher} disabled={!temPreco && !isEscolhido} className="mt-2 w-full min-h-[44px]">
        {isEscolhido ? (
          <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5" />Escolhido</span>
        ) : (
          'Escolher este fornecedor'
        )}
      </Button>
    </div>
  );
}
